import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useFormContext, STEP_IDS, backgroundImages } from './FormContext';
import { StepIndicator } from './StepIndicator';
import { AnimatedStepTransition } from './AnimatedStepTransition';
import { StepGuide } from './StepGuide';
import { StepRenderer } from './StepRenderer';
export const FormContainer: React.FC = () => {
  const {
    currentStep,
    visibleSteps,
    formSubmitted,
    direction,
    isAnimating,
    goToNextStep,
    goToPreviousStep,
    isCurrentStepValid,
    getStepNumber,
    getTotalSteps,
    calculateProgress,
    getStepGuideText
  } = useFormContext();
  // Mobile swipe detection
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diffX = touchStartX.current - touchEndX.current;
    const threshold = 100; // minimum distance to be considered a swipe
    if (Math.abs(diffX) > threshold) {
      if (diffX > 0 && isCurrentStepValid()) {
        // Swiped left - go to next step
        goToNextStep();
      } else if (diffX < 0 && currentStep !== STEP_IDS.WELCOME) {
        // Swiped right - go to previous step
        goToPreviousStep();
      }
    }
    // Reset values
    touchStartX.current = null;
    touchEndX.current = null;
  };
  // Calculate progress for display
  const progress = calculateProgress();
  const stepNumber = getStepNumber();
  const totalSteps = getTotalSteps();
  // Get the background image for the current step
  const currentBackground = backgroundImages[Math.min(currentStep % backgroundImages.length, backgroundImages.length - 1)];
  return <>
      {/* Fullscreen background with overlay */}
      <div className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000" style={{
      backgroundImage: `url(${currentBackground})`
    }} data-id="element-733">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70 backdrop-blur-sm" data-id="element-734"></div>
      </div>
      {/* Main content */}
      <main className="flex-grow flex items-center justify-center relative z-10 py-20 px-4" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-735">
        <div className="w-full max-w-3xl mx-auto" data-id="element-736">
          {/* Progress bar - only show after welcome screen */}
          {currentStep !== STEP_IDS.WELCOME && !formSubmitted && <StepIndicator currentStep={stepNumber} totalSteps={totalSteps} progress={progress} data-id="element-737" />}
          {/* Navigation arrows for desktop */}
          {currentStep !== STEP_IDS.WELCOME && !formSubmitted && <div className="hidden md:flex justify-between absolute left-0 right-0 top-1/2 transform -translate-y-1/2 px-4 w-full max-w-4xl mx-auto" data-id="element-738">
              {currentStep !== visibleSteps[1] && <button onClick={goToPreviousStep} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white transition-all duration-300 flex items-center justify-center" data-id="element-739">
                  <ChevronLeft className="h-6 w-6" data-id="element-740" />
                </button>}
              {currentStep !== visibleSteps[visibleSteps.length - 1] && isCurrentStepValid() && <button onClick={goToNextStep} className="w-10 h-10 rounded-full bg-[#CDA735]/80 hover:bg-[#CDA735] backdrop-blur-md text-white transition-all duration-300 flex items-center justify-center ml-auto" data-id="element-741">
                    <ChevronRight className="h-6 w-6" data-id="element-742" />
                  </button>}
            </div>}
          {/* Form container */}
          <div className="bg-white/10 backdrop-blur-md rounded-xl overflow-hidden shadow-xl border border-white/20 transition-all duration-500" data-id="element-743">
            {/* Step Guide */}
            {currentStep !== STEP_IDS.WELCOME && !formSubmitted && getStepGuideText() && <StepGuide text={getStepGuideText()} data-id="element-744" />}
            {/* Animated Step Transition Container */}
            <AnimatedStepTransition isAnimating={isAnimating} direction={direction} data-id="element-745">
              <StepRenderer data-id="element-746" />
            </AnimatedStepTransition>
          </div>
          {/* Mobile swipe indicator */}
          {currentStep !== STEP_IDS.WELCOME && !formSubmitted && <div className="mt-6 md:hidden text-center text-white/60 text-sm" data-id="element-747">
              <p data-id="element-748">Swipe naar links of rechts om te navigeren</p>
              <div className="flex justify-center items-center gap-4 mt-2" data-id="element-749">
                <ChevronLeft className="h-4 w-4" data-id="element-750" />
                <span data-id="element-751">Vorige</span>
                <span className="mx-2" data-id="element-752">|</span>
                <span data-id="element-753">Volgende</span>
                <ChevronRight className="h-4 w-4" data-id="element-754" />
              </div>
            </div>}
        </div>
      </main>
    </>;
};